import React, { useEffect, useState } from 'react';
import { ArrowRight, Download, MoreVertical, Share2, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

type MobilePlatform = 'android' | 'ios';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

const DISMISS_STORAGE_KEY = 'agrisafe:pwa-install-dismissed-at';
const INSTALLED_STORAGE_KEY = 'agrisafe:pwa-install-installed';
const DISMISS_FOR_MS = 7 * 24 * 60 * 60 * 1000;
const EMBEDDED_WEBVIEW_PATTERN = /\bwv\b|FBAN|FBAV|Instagram|Line\/|GSA\/|Twitter|TikTok|Snapchat/i;

const isEmbeddedWebView = (userAgent: string, isIos: boolean): boolean => {
  if (EMBEDDED_WEBVIEW_PATTERN.test(userAgent)) return true;
  if (!isIos) return false;

  return !/Safari\/|CriOS\/|FxiOS\/|EdgiOS\/|OPiOS\//i.test(userAgent);
};

const detectMobilePlatform = (): MobilePlatform | null => {
  const userAgent = navigator.userAgent || '';
  const isIos = /iPad|iPhone|iPod/.test(userAgent)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  if (isEmbeddedWebView(userAgent, isIos)) return null;
  if (isIos) return 'ios';
  if (/Android/i.test(userAgent)) return 'android';
  return null;
};

const isStandalone = (): boolean => {
  const navigatorStandalone = Boolean((navigator as Navigator & { standalone?: boolean }).standalone);
  return navigatorStandalone || window.matchMedia('(display-mode: standalone)').matches;
};

const wasRecentlyDismissed = (): boolean => {
  const value = window.localStorage.getItem(DISMISS_STORAGE_KEY);
  if (!value) return false;
  const timestamp = Number(value);
  return Number.isFinite(timestamp) && Date.now() - timestamp < DISMISS_FOR_MS;
};

const hasInstalledMarker = (): boolean =>
  window.localStorage.getItem(INSTALLED_STORAGE_KEY) === 'true';

const markInstalled = () => {
  window.localStorage.setItem(INSTALLED_STORAGE_KEY, 'true');
  window.localStorage.removeItem(DISMISS_STORAGE_KEY);
};

const PwaInstallPrompt: React.FC = () => {
  const { t } = useTranslation('common');
  const [platform, setPlatform] = useState<MobilePlatform | null>(null);
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const mobilePlatform = detectMobilePlatform();
    setPlatform(mobilePlatform);

    if (!mobilePlatform || isStandalone()) {
      return undefined;
    }

    let fallbackTimer: number | undefined;
    const clearFallbackTimer = () => {
      if (fallbackTimer !== undefined) {
        window.clearTimeout(fallbackTimer);
        fallbackTimer = undefined;
      }
    };

    const handleBeforeInstallPrompt = (event: Event) => {
      const installEvent = event as BeforeInstallPromptEvent;
      installEvent.preventDefault();
      clearFallbackTimer();

      // A fresh installability event means a previous installed marker is stale
      // (for example after uninstalling the PWA from the device).
      window.localStorage.removeItem(INSTALLED_STORAGE_KEY);

      if (wasRecentlyDismissed()) {
        setDeferredPrompt(null);
        setVisible(false);
        return;
      }

      setDeferredPrompt(installEvent);
      setVisible(true);
    };

    const handleInstalled = () => {
      clearFallbackTimer();
      markInstalled();
      setDeferredPrompt(null);
      setVisible(false);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleInstalled);

    if (mobilePlatform === 'ios') {
      if (!wasRecentlyDismissed()) {
        setVisible(true);
      }
    } else if (!wasRecentlyDismissed() && !hasInstalledMarker()) {
      fallbackTimer = window.setTimeout(() => {
        fallbackTimer = undefined;
        if (!wasRecentlyDismissed() && !hasInstalledMarker()) {
          setVisible(true);
        }
      }, 1500);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleInstalled);
      clearFallbackTimer();
    };
  }, []);

  const dismiss = () => {
    window.localStorage.setItem(DISMISS_STORAGE_KEY, String(Date.now()));
    setVisible(false);
  };

  const install = async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    if (choice.outcome === 'accepted') {
      markInstalled();
      setVisible(false);
    } else {
      dismiss();
    }
  };

  if (!visible || !platform || isStandalone()) return null;

  return (
    <aside
      role="dialog"
      aria-live="polite"
      aria-label={`${t('add')} ${t('appName')}`}
      data-testid="pwa-install-prompt"
      className="fixed inset-x-4 bottom-24 z-40 mx-auto max-w-md rounded-2xl border border-[var(--border-light)] bg-[var(--surface)] p-4 shadow-2xl"
    >
      <div className="flex items-start gap-3">
        <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[var(--color-primary)] text-white">
          {platform === 'ios' ? (
            <Share2 className="h-5 w-5" aria-hidden="true" />
          ) : (
            <Download className="h-5 w-5" aria-hidden="true" />
          )}
        </div>

        <div className="min-w-0 flex-1">
          <h2 className="text-base font-semibold text-[var(--text-primary)]">
            {t('add')} {' '} {t('appName')}
          </h2>

          {platform === 'ios' ? (
            <div className="mt-3 rounded-xl border border-[var(--border-light)] bg-muted p-3">
              <div className="flex items-start gap-2 text-sm font-medium leading-5 text-[var(--text-primary)]">
                <Share2 className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
                <span>{t('pwaInstallIosInstruction')}</span>
              </div>
              <div className="mt-2 flex items-center gap-2 text-sm text-[var(--text-secondary)]">
                <ArrowRight className="h-4 w-4 shrink-0 rtl:rotate-180" aria-hidden="true" />
                <span>{t('add')} {t('appName')}</span>
              </div>
            </div>
          ) : (
            <div className="mt-2 flex items-center gap-2 text-sm text-[var(--text-secondary)]">
              {deferredPrompt ? (
                <span>{t('appName')}</span>
              ) : (
                <>
                  <MoreVertical className="h-5 w-5 shrink-0" aria-hidden="true" />
                  <span>{t('more')}</span>
                  <ArrowRight className="h-4 w-4 shrink-0 rtl:rotate-180" aria-hidden="true" />
                  <Download className="h-5 w-5 shrink-0" aria-hidden="true" />
                  <span>{t('add')} {t('appName')}</span>
                </>
              )}
            </div>
          )}

          <div className="mt-4 flex gap-2">
            {deferredPrompt && (
              <button
                type="button"
                onClick={() => void install()}
                className="min-h-[42px] flex-1 rounded-lg bg-[var(--color-primary)] px-4 py-2 text-sm font-semibold text-white"
                data-testid="pwa-install-action"
              >
                {t('add')}
              </button>
            )}
            <button
              type="button"
              onClick={dismiss}
              className="min-h-[42px] flex-1 rounded-lg border border-[var(--border-light)] px-4 py-2 text-sm font-semibold text-[var(--text-primary)]"
              data-testid="pwa-install-dismiss"
            >
              {t('close')}
            </button>
          </div>
        </div>

        <button
          type="button"
          onClick={dismiss}
          aria-label={t('close')}
          className="rounded-md p-1 text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
        >
          <X className="h-5 w-5" aria-hidden="true" />
        </button>
      </div>
    </aside>
  );
};

export default PwaInstallPrompt;
